import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area 
} from 'recharts';
import { 
  Users, Activity, TrendingUp, AlertTriangle, ChevronRight, MapPin, 
  Building2, Shield, UserCog, Globe, RefreshCw, Bell, Layers, Download
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const SuperAdminDashboard = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setRefreshing(true);
      const res = await api.getSuperAdminDashboard();
      setDashboard(res.data);
    } catch (error) {
      console.error('Error fetching super admin dashboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleExportICDS = async () => {
    try {
      setExporting(true);
      const response = await api.exportICDSReport();
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ICDS_ECD_Report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting ICDS report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const riskChartData = [
    { name: t('admin.lowRisk'), value: dashboard?.risk_distribution?.low || 0, color: '#10B981' },
    { name: t('admin.moderateRisk'), value: dashboard?.risk_distribution?.moderate || 0, color: '#F59E0B' },
    { name: t('admin.highRisk'), value: dashboard?.risk_distribution?.high || 0, color: '#EF4444' }
  ];

  const userByRoleData = dashboard?.user_by_role ? [
    { name: t('auth.caregiver'), value: dashboard.user_by_role.caregiver || 0, fill: '#3B82F6' },
    { name: t('auth.frontlineWorker'), value: dashboard.user_by_role.frontline_worker || 0, fill: '#10B981' },
    { name: t('auth.districtAdmin'), value: dashboard.user_by_role.district_admin || 0, fill: '#8B5CF6' },
    { name: t('auth.stateAdmin'), value: dashboard.user_by_role.state_admin || 0, fill: '#F59E0B' },
    { name: t('auth.superAdmin'), value: dashboard.user_by_role.super_admin || 0, fill: '#EF4444' }
  ] : [];

  const topStatesData = dashboard?.top_states?.map(state => ({
    name: state.code,
    fullName: state.name,
    children: state.children_count,
    screenings: state.screenings_count,
    highRisk: state.high_risk_count,
    users: state.users_count
  })) || [];

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-[#0F4C81]/5 to-[#F59E0B]/5" data-testid="super-admin-dashboard">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-[#0F4C81] to-[#1E40AF] p-3 rounded-2xl shadow-lg">
              <Globe className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#111827] font-['Nunito']" data-testid="dashboard-title">
                National Overview
              </h1>
              <p className="text-base text-[#4B5563]">
                Super Admin Control Center - All India Statistics
              </p>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col md:flex-row gap-3">
            <button
              onClick={fetchDashboard}
              disabled={refreshing}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-white text-[#0F4C81] rounded-xl font-semibold border-2 border-[#0F4C81] hover:bg-[#0F4C81] hover:text-white transition-colors"
              data-testid="refresh-button"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => navigate('/user-management')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#10B981] text-white rounded-xl font-semibold hover:bg-[#059669] transition-colors shadow-md"
              data-testid="user-management-button"
            >
              <UserCog size={18} />
              <span>{t('admin.userManagement')}</span>
            </button>
            <button
              onClick={() => navigate('/bulk-user-upload')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#8B5CF6] text-white rounded-xl font-semibold hover:bg-[#7C3AED] transition-colors shadow-md"
              data-testid="bulk-user-upload-button"
            >
              <Users size={18} />
              <span>Bulk Add Users</span>
            </button>
            <button
              onClick={() => navigate('/audit-logs')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors shadow-md"
              data-testid="audit-logs-button"
            >
              <Shield size={18} />
              <span>{t('admin.viewAuditLogs')}</span>
            </button>
            <button
              onClick={() => navigate('/admin/create-user')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#F59E0B] text-white rounded-xl font-semibold hover:bg-[#D97706] transition-colors shadow-md"
              data-testid="create-user-button"
            >
              <UserCog size={18} />
              <span>Create User</span>
            </button>
            <button
              onClick={handleExportICDS}
              disabled={exporting}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#059669] text-white rounded-xl font-semibold hover:bg-[#047857] transition-colors shadow-md"
              data-testid="icds-export-button"
            >
              <Download size={18} className={exporting ? 'animate-pulse' : ''} />
              <span>{exporting ? 'Exporting...' : 'ICDS Export'}</span>
            </button>
          </div>
        </div>

        {/* National Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-total-users">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-[#3B82F6]" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{dashboard?.national_overview?.total_users || 0}</span>
            </div>
            <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider">Total Users</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-total-children">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-[#8B5CF6]" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{dashboard?.national_overview?.total_children || 0}</span>
            </div>
            <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.totalChildren')}</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-total-screenings">
            <div className="flex items-center justify-between mb-2">
              <Activity className="text-[#10B981]" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{dashboard?.national_overview?.total_screenings || 0}</span>
            </div>
            <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider">{t('admin.screeningsDone')}</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-interventions">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-[#F59E0B]" size={28} />
              <span className="text-2xl font-bold text-[#111827]">{dashboard?.national_overview?.total_interventions || 0}</span>
            </div>
            <p className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.interventions')}</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-green-200 bg-green-50 hover:shadow-md transition-shadow" data-testid="stat-active-states">
            <div className="flex items-center justify-between mb-2">
              <MapPin className="text-[#10B981]" size={28} />
              <span className="text-2xl font-bold text-[#10B981]">{dashboard?.national_overview?.states_active || 0}</span>
            </div>
            <p className="text-xs font-semibold text-[#065F46] uppercase tracking-wider">Active States</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Risk Distribution */}
          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="risk-distribution-chart">
            <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('admin.riskDistribution')}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={riskChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {riskChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Users by Role */}
          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="users-by-role-chart">
            <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito']">Users by Role</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={userByRoleData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {userByRoleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top States Performance */}
        <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100 mb-8" data-testid="top-states-chart">
          <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito'] flex items-center">
            <Layers className="mr-2 text-[#0F4C81]" size={24} />
            Top States by Activity
          </h3>
          {topStatesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topStatesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [value, name === 'children' ? 'Children' : name === 'screenings' ? 'Screenings' : 'High Risk']}
                  labelFormatter={(label) => topStatesData.find(s => s.name === label)?.fullName || label}
                />
                <Legend />
                <Bar dataKey="children" name="Children" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="screenings" name="Screenings" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="highRisk" name="High Risk" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-[#6B7280]">
              <MapPin size={48} className="mx-auto mb-4 opacity-50" />
              <p>No state data available yet</p>
            </div>
          )}
        </div>

        {/* State-wise Details Table */}
        <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100 mb-8" data-testid="state-details-table">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-[#111827] font-['Nunito'] flex items-center">
              <MapPin className="mr-2 text-[#0F4C81]" size={24} />
              State-wise Statistics
            </h3>
            <span className="text-sm text-[#6B7280]">
              {dashboard?.state_stats?.length || 0} states with data
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-bold text-[#374151] uppercase">State</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-[#374151] uppercase">Children</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-[#374151] uppercase">Users</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-[#374151] uppercase">Screenings</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-[#374151] uppercase">High Risk</th>
                  <th className="text-center py-3 px-4 text-sm font-bold text-[#374151] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {dashboard?.state_stats?.slice(0, 15).map((state, index) => (
                  <tr key={state.code} className={`border-b border-slate-100 hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-[#111827]">{state.name}</span>
                        <span className="text-xs text-[#6B7280] bg-slate-100 px-2 py-0.5 rounded">{state.code}</span>
                      </div>
                    </td>
                    <td className="text-center py-3 px-4 font-semibold text-[#3B82F6]">{state.children_count}</td>
                    <td className="text-center py-3 px-4 font-semibold text-[#8B5CF6]">{state.users_count}</td>
                    <td className="text-center py-3 px-4 font-semibold text-[#10B981]">{state.screenings_count}</td>
                    <td className="text-center py-3 px-4">
                      {state.high_risk_count > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                          <AlertTriangle size={12} className="mr-1" />
                          {state.high_risk_count}
                        </span>
                      ) : (
                        <span className="text-[#6B7280]">-</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-4">
                      <button 
                        onClick={() => navigate(`/dashboard?state=${encodeURIComponent(state.name)}`)}
                        className="text-[#0F4C81] hover:text-[#0d3d6b] font-medium text-sm hover:underline"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
                {(!dashboard?.state_stats || dashboard.state_stats.length === 0) && (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-[#6B7280]">
                      No state data available. Children will appear here once registered.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent High Risk Alerts */}
        <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-red-100" data-testid="recent-alerts">
          <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito'] flex items-center">
            <Bell className="mr-2 text-[#EF4444]" size={24} />
            Recent High Risk Alerts (Last 7 Days)
          </h3>
          
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {dashboard?.recent_high_risk_alerts?.map((alert, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="flex items-center space-x-4">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <AlertTriangle className="text-[#EF4444]" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#111827]">{alert.child_name || 'Unknown Child'}</p>
                    <p className="text-sm text-[#6B7280]">
                      {alert.state && alert.district ? `${alert.district}, ${alert.state}` : 'Location not specified'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-block px-3 py-1 bg-red-500 text-white rounded-full text-xs font-bold">
                    HIGH RISK
                  </span>
                  <p className="text-xs text-[#6B7280] mt-1">
                    {alert.created_at ? new Date(alert.created_at).toLocaleDateString() : 'Date unknown'}
                  </p>
                </div>
              </div>
            ))}
            {(!dashboard?.recent_high_risk_alerts || dashboard.recent_high_risk_alerts.length === 0) && (
              <div className="text-center py-8 text-[#6B7280]">
                <AlertTriangle size={32} className="mx-auto mb-2 opacity-50" />
                <p>No high-risk alerts in the last 7 days</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    
    {/* Copyright Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default SuperAdminDashboard;
